import React, { useState, useEffect, useMemo, useRef, useLayoutEffect, useCallback } from 'react';
import { defineMessages } from 'react-intl-next';

import { cssMap } from '@atlaskit/css';

import { useStoreOptions } from '@confluence/content-smartlinks/entry-points/useStoreOptions.ts';
import { isPageSSRd } from '@confluence/content-smartlinks/entry-points/isPageSSRd.ts';
import { ConfluenceSmartCardProvider } from '@confluence/smart-card-provider/entry-points/ConfluenceSmartCardProvider.tsx';

import type { SpaceOverviewSwitcherQuery$data } from '../../__generated__/SpaceOverviewSwitcherQuery.graphql.ts';
import { Card } from '../components/Card.tsx';

import { applyTopLinksPinToggle } from './applyTopLinksPinToggle.ts';
import { LinkPickerButton } from './LinkPickerButton.tsx';
import { LinkItem as Item, type LinkData } from './LinkItem.tsx';

const i18n = defineMessages({
	topDocs: {
		id: 'space-overview.smart-space-overview.top-docs-card.title',
		defaultMessage: 'Top docs',
		description: 'Title of the top docs card',
	},
});

type SmartSpaceOverviewFromSwitcher = NonNullable<
	SpaceOverviewSwitcherQuery$data['confluence_smartSpaceOverview']
>;

type TopLinksCardProps = {
	topLinks: SmartSpaceOverviewFromSwitcher['topLinks'];
};

const topLinksItemWrapperStyles = cssMap({
	itemWrapper: {
		flex: 1,
		minWidth: '0px',
		position: 'relative',
		transformOrigin: '50% 50%',
	},
});

const TAB_PIN_FLIP_MS = 200;
const TAB_PIN_FLIP_EASING = 'cubic-bezier(0.2, 0.85, 0.3, 1)';
const TAB_PIN_COMPRESS_SCALE = 0.86;

const maxCount = 4;

export const TopLinksCard = ({ topLinks }: TopLinksCardProps) => {
	const queryLinks = useMemo<LinkData[]>(() => {
		const rawLinks = topLinks?.links;
		if (!rawLinks) {
			return [];
		}
		const mapped = rawLinks.reduce<LinkData[]>((acc, link) => {
			if (link?.url && link?.title) {
				acc.push({
					id: link.url,
					url: link.url,
					title: link.title,
					isPinned: link.isPinned ?? false,
				});
			}
			return acc;
		}, []);
		return mapped.slice(0, maxCount);
	}, [topLinks]);

	const [links, setLinks] = useState<LinkData[]>(queryLinks);

	const linksRef = useRef(links);
	linksRef.current = links;

	const itemWrapperRefs = useRef(new Map<string, HTMLDivElement>());
	const prevItemRectsRef = useRef(new Map<string, DOMRectReadOnly>());
	const runTabReorderFlipRef = useRef(false);
	const primaryMotionLinkIdRef = useRef<string | null>(null);

	useEffect(() => {
		setLinks(queryLinks);
	}, [queryLinks]);
	const isAtCapacity = links.length >= maxCount;

	const handleAddLink = () => {};

	const handleLinkSubmit = (linkData: { url: string; title: string }) => {
		const newLink: LinkData = {
			id: `${linkData.url}-${linkData.title}`,
			url: linkData.url,
			title: linkData.title,
			subtitle: new URL(linkData.url).hostname,
			isPinned: true,
		};

		setLinks((prevLinks) => [...prevLinks, newLink]);
	};

	const handleRemoveLink = (linkId: string) => {
		setLinks((prevLinks) => prevLinks.filter((link) => link.id !== linkId));
	};

	const handlePinLink = (linkId: string) => {
		setLinks((prevLinks) => {
			const { nextLinks, didReorder } = applyTopLinksPinToggle(prevLinks, linkId);
			if (didReorder) {
				runTabReorderFlipRef.current = true;
				primaryMotionLinkIdRef.current = linkId;
			}
			return nextLinks;
		});
	};

	useLayoutEffect(() => {
		const currentRects = new Map<string, DOMRectReadOnly>();
		for (const link of links) {
			const el = itemWrapperRefs.current.get(link.id);
			if (el) {
				currentRects.set(link.id, el.getBoundingClientRect());
			}
		}

		if (runTabReorderFlipRef.current) {
			runTabReorderFlipRef.current = false;
			const primaryMotionId = primaryMotionLinkIdRef.current;
			const elementsToAnimate = new Set<HTMLDivElement>();
			for (const link of links) {
				const el = itemWrapperRefs.current.get(link.id);
				const prev = prevItemRectsRef.current.get(link.id);
				const curr = currentRects.get(link.id);
				if (!el || !prev || !curr) {
					continue;
				}
				const dx = prev.left - curr.left;
				const dy = prev.top - curr.top;
				if (Math.abs(dx) > 0.5 || Math.abs(dy) > 0.5) {
					const isPrimaryMotionTile = primaryMotionId !== null && link.id === primaryMotionId;
					const compress = isPrimaryMotionTile ? ` scale(${TAB_PIN_COMPRESS_SCALE})` : '';
					el.style.transition = 'none';
					el.style.willChange = 'transform';
					el.style.zIndex = isPrimaryMotionTile ? '3' : '2';
					el.style.transform = `translate(${dx}px, ${dy}px)${compress}`;
					elementsToAnimate.add(el);
				}
			}

			if (elementsToAnimate.size > 0) {
				requestAnimationFrame(() => {
					requestAnimationFrame(() => {
						for (const el of elementsToAnimate) {
							el.style.transition = `transform ${TAB_PIN_FLIP_MS}ms ${TAB_PIN_FLIP_EASING}`;
							el.style.transform = '';
						}

						window.setTimeout(() => {
							for (const el of elementsToAnimate) {
								el.style.transition = '';
								el.style.transform = '';
								el.style.willChange = '';
								el.style.zIndex = '';
							}
							primaryMotionLinkIdRef.current = null;
						}, TAB_PIN_FLIP_MS + 50);
					});
				});
			} else {
				primaryMotionLinkIdRef.current = null;
			}
		}

		prevItemRectsRef.current = currentRects;
	}, [links]);

	const renderTopLinkItemWrapper = useCallback((child: React.ReactElement, index: number) => {
		const linkId = linksRef.current[index]?.id ?? (child.key != null ? String(child.key) : '');
		return (
			<div
				key={linkId || (child.key ?? index)}
				css={topLinksItemWrapperStyles.itemWrapper}
				ref={(el) => {
					if (!linkId) {
						return;
					}
					if (el) {
						itemWrapperRefs.current.set(linkId, el);
					} else {
						itemWrapperRefs.current.delete(linkId);
					}
				}}
			>
				{child}
			</div>
		);
	}, []);

	const initialState = window['__SSR_TOP_DOCS__'];
	const storeData = useStoreOptions(initialState, isPageSSRd());
	const storeOptions = isPageSSRd() ? storeData : {};

	return (
		<ConfluenceSmartCardProvider authFlow="disabled" {...storeOptions}>
			<Card
				title={i18n.topDocs}
				count={links.length}
				maxCount={maxCount}
				renderItemWrapper={renderTopLinkItemWrapper}
				actionButton={
					<LinkPickerButton
						onAddClick={handleAddLink}
						onLinkSubmit={handleLinkSubmit}
						disabled={isAtCapacity}
						existingLinks={links}
					/>
				}
			>
				{links.map((link) => (
					<Item
						key={link.id}
						link={link}
						onRemove={handleRemoveLink}
						onPin={handlePinLink}
						isPinned={link.isPinned}
					/>
				))}
			</Card>
		</ConfluenceSmartCardProvider>
	);
};
